import { Parser } from 'expr-eval';
import type { MetricColumnConfig } from '../../../config/tpoGenConfig';

const parser = new Parser();

type DependencyGraph = Record<string, string[]>;

export type CascadeResult = {
    values: Record<string, number>;
    cascadedKeys: Set<string>;
};

function buildDependencyGraph(configs: MetricColumnConfig[]): DependencyGraph {
    const graph: DependencyGraph = {};

    for (const config of configs) {
        if (!config.formula || !config.dependencies) continue;
        for (const dep of config.dependencies) {
            if (!graph[dep]) graph[dep] = [];
            if (!graph[dep].includes(config.key)) {
                graph[dep].push(config.key);
            }
        }
    }
    return graph;
}

function buildFormulaMap(configs: MetricColumnConfig[]): Record<string, string> {
    const map: Record<string, string> = {};
    for (const config of configs) {
        if (config.formula) {
            map[config.key] = config.formula;
        }
    }
    return map;
}

/**
 * Shared expression evaluator used by both the metric DAG (`cascadeKPIs`) and
 * the form -> metric cascade (`applyFormCascade`). Identifiers are substituted
 * from `values` (missing / non-numeric resolve to 0); non-finite results
 * collapse to 0. Keeping a single evaluator guarantees form-driven seeds and
 * metric formulas behave identically.
 */
export function evaluateExpression(expression: string, values: Record<string, number>): number {
    const substituted = expression.replace(/\b[A-Za-z_]+\b/g, (match) => {
        const val = values[match];
        const numericVal = val !== undefined && !isNaN(Number(val)) ? Number(val) : 0;
        return numericVal.toString();
    });

    let evaluatedValue = parser.evaluate(substituted);

    if (!isFinite(evaluatedValue) || isNaN(evaluatedValue)) {
        evaluatedValue = 0;
    }

    return evaluatedValue;
}

export function cascadeKPIs(
    configs: MetricColumnConfig[],
    changedKey: string,
    newValue: number,
    currentValues: Record<string, number>,
): CascadeResult {
    const dependencyGraph = buildDependencyGraph(configs);
    const formulaMap = buildFormulaMap(configs);
    const values = { ...currentValues, [changedKey]: newValue };
    const cascadedKeys = new Set<string>();

    const updateDependencies = (kpi: string) => {
        const dependents = dependencyGraph[kpi];
        if (!dependents) return;

        for (const dependentKpi of dependents) {
            const formula = formulaMap[dependentKpi];
            if (!formula) continue;

            try {
                values[dependentKpi] = evaluateExpression(formula, values);
                cascadedKeys.add(dependentKpi);
                updateDependencies(dependentKpi);
            } catch {
                values[dependentKpi] = 0;
                cascadedKeys.add(dependentKpi);
            }
        }
    };

    updateDependencies(changedKey);
    return { values, cascadedKeys };
}
